import { useState, useEffect, useReducer, useContext } from "react";
import openSocket from "../../services/socket-io";
import toastError from "../../errors/toastError";

import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_WHATSAPPS") {
    const whatsApps = action.payload;
    return [...whatsApps];
  }

  if (action.type === "UPDATE_WHATSAPPS") {
    const whatsApp = action.payload;
    const whatsAppIndex = state.findIndex(s => s.id === whatsApp.id);

    if (whatsAppIndex !== -1) {
      const nextState = [...state];
      nextState[whatsAppIndex] = {
        ...nextState[whatsAppIndex],
        ...whatsApp
      };
      return nextState;
    }

    return [whatsApp, ...state];
  }

  if (action.type === "UPDATE_SESSION") {
    const whatsAppSession = action.payload;
    const whatsAppIndex = state.findIndex(s => s.id === whatsAppSession.id);

    if (whatsAppIndex !== -1) {
      const nextState = [...state];
      nextState[whatsAppIndex] = {
        ...nextState[whatsAppIndex],
        ...whatsAppSession
      };
      return nextState;
    }

    return [whatsAppSession, ...state];
  }

  if (action.type === "DELETE_WHATSAPPS") {
    const whatsAppId = action.payload;
    return state.filter(s => s.id !== whatsAppId);
  }

  if (action.type === "RESET") {
    return [];
  }

  return state;
};

const useWhatsApps = () => {
  const [whatsApps, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(true);
  const { isAuth, loading: authLoading } = useContext(AuthContext);

  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (!isAuth) {
      dispatch({ type: "RESET" });
      setLoading(false);
      return;
    }

    setLoading(true);
    const fetchSession = async () => {
      try {
        const { data } = await api.get("/whatsapp/");
        dispatch({ type: "LOAD_WHATSAPPS", payload: data });
        setLoading(false);
      } catch (err) {
        setLoading(false);
        const status = err?.response?.status;
        const errorCode = err?.response?.data?.error;
        const shouldIgnoreAuthError =
          status === 401 ||
          status === 403 ||
          errorCode === "ERR_SESSION_EXPIRED";

        if (!shouldIgnoreAuthError) {
          toastError(err);
        }
      }
    };
    fetchSession();
  }, [isAuth, authLoading]);

  useEffect(() => {
    if (authLoading || !isAuth) {
      return undefined;
    }

    const socket = openSocket();

    socket.on("whatsapp", data => {
      if (data.action === "update") {
        dispatch({ type: "UPDATE_WHATSAPPS", payload: data.whatsapp });
      }
    });

    socket.on("whatsapp", data => {
      if (data.action === "delete") {
        dispatch({ type: "DELETE_WHATSAPPS", payload: data.whatsappId });
      }
    });

    socket.on("whatsappSession", data => {
      if (data.action === "update") {
        dispatch({ type: "UPDATE_SESSION", payload: data.session });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [isAuth, authLoading]);

  return { whatsApps, loading };
};

export default useWhatsApps;
